import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IMAGES, VALUES } from '../mock';
import Newsletter from '../components/Newsletter';
import SEO from '../components/SEO';

export default function UeberUns() {
  return (
    <div>
      <SEO
        path="/ueber-uns"
        title="Über uns – Pascal Schwab, Geschäftsführer"
        description="Pascal Schwab, Geschäftsführer der Moonshine Solutions GmbH – dein persönlicher Experte für inflationsgeschützten und steueroptimierten Vermögensaufbau in der Schweiz."
      />
      <section className="relative pt-36 pb-16 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-4">Über uns</div>
          <h1 className="font-serif-display text-5xl md:text-7xl text-[#f5efe3] leading-[1.05]">
            Pascal Schwab – <span className="italic text-gold-grad">Geschäftsführer</span>
          </h1>
          <p className="mt-6 text-[#9a9285] text-lg max-w-3xl">
            Dein persönlicher Experte für inflationsgeschützten und steueroptimierten Vermögensaufbau.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-16">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#d4b574]/15 to-transparent rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden border border-[#d4b574]/15">
                <img src={IMAGES.pascal} alt="Pascal Schwab" className="w-full h-auto" />
              </div>
            </div>
            <div className="mt-6 p-6 rounded-2xl border border-white/8 bg-[#0e0e0c]">
              <div className="font-serif-display text-xl text-[#e8d5a8]">Pascal Schwab</div>
              <div className="text-sm text-[#9a9285] mt-1">Geschäftsführer Moonshine Solutions GmbH</div>
              <blockquote className="mt-4 pl-4 border-l-2 border-[#d4b574]/50 font-serif-display italic text-[#cfc6b5] leading-snug">
                „Alles was ich will ist mein eigenes Leben zu gestalten und dabei anderen helfen, ihr volles Potenzial zu entfalten.“
              </blockquote>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6 text-[#cfc6b5] leading-relaxed">
            <p>
              Als Familienvater weiß ich aus erster Hand, welche Herausforderungen das Leben im finanziellen Bereich mit sich bringen kann. Bereits früh habe ich mich intensiv mit diesem Thema auseinandergesetzt, denn ich erkannte die Bedeutung <span className="text-[#e8d5a8]">finanzieller Stabilität</span> für das Wohlergehen meiner Familie und mich.
            </p>
            <p>
              Mit meiner langjährigen Erfahrung und meinem fundierten Wissen stehe ich Dir zur Seite, um <span className="text-[#e8d5a8]">massgeschneiderte Lösungen</span> für Deine individuelle Situation zu entwickeln. Mein Ziel: gemeinsam mit Dir finanzielle Stabilität und langfristigen Wohlstand zu erreichen.
            </p>
            <p>
              Dabei lege ich besonderen Wert auf nachhaltige Strategien, die nicht nur kurzfristige Gewinne, sondern langfristige Sicherheit und Wachstum bieten. Durch kontinuierliche Weiterbildung und die Nutzung innovativer Ansätze halte ich mich stets auf dem neuesten Stand.
            </p>

            <div className="divider-gold my-8" />

            <h2 className="font-serif-display text-3xl md:text-4xl text-[#f5efe3]">Meine <span className="italic text-gold-grad">Motivation</span></h2>
            <p>
              Die Welt des finanziellen Erfolgs war für mich nicht immer zugänglich. Es gab Zeiten, in denen meine finanzielle Situation mich vor große Herausforderungen stellte. In einem herkömmlichen 9-to-5-Job fragte ich mich oft, ob das alles war – das monotone <em>Hamsterrad des Überlebens</em> schien mich zu ersticken.
            </p>
            <p>
              Diese Sehnsucht trieb mich an, mich intensiv mit Inflationsschutz, Steueroptimierung und Vermögensaufbau zu beschäftigen. Ich wollte verstehen, wie ich mein Geld für mich arbeiten lassen kann – anstatt mein ganzes Leben für Geld zu arbeiten.
            </p>
            <p className="font-serif-display text-2xl text-[#e8d5a8] leading-snug">
              Ich will andere Menschen dazu motivieren, ihre eigene Erfolgsgeschichte zu schreiben.
            </p>
            <p>
              Wenn auch du das Gefühl hast, dass da draußen mehr auf Dich wartet, dann lade ich Dich ein, Dich mir anzuschließen. Lass uns gemeinsam den Weg zu finanzieller Freiheit und persönlicher Erfüllung beschreiten. Denn das Leben ist zu kurz, um es nur zu überleben – <span className="text-[#e8d5a8]">lass uns gemeinsam leben.</span>
            </p>
            <Link to="/kontakt" className="mt-4 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors">
              Zum Kontaktformular <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-10 border-y border-white/5 bg-[#08080a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Werte</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">Wofür wir <span className="italic text-gold-grad">stehen</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {VALUES.map((v, i) => (
              <div key={i} className="text-center">
                <div className="font-serif-display text-3xl text-gold-grad">0{i + 1}</div>
                <div className="mt-2 text-sm uppercase tracking-[0.22em] text-[#e8d5a8]">{v.title}</div>
                <div className="mt-2 text-xs text-[#9a9285] leading-relaxed">{v.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif-display text-4xl text-[#f5efe3]">Was noch <span className="italic text-gold-grad">..?</span></h2>
          <p className="mt-6 text-[#9a9285] leading-relaxed">
            Zusätzlich zu meiner Expertise in Inflationsschutz, Steueroptimierung und Vermögensaufbau biete ich Seminare und Workshops, sowie persönliche Beratungsgespräche an. Ich bin Teil eines Netzwerks von Finanzexperten und Investoren – davon profitierst auch du.
          </p>
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
